import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Award, Briefcase, Instagram, PhoneCall } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { siteConfig } from '../config/site';
import { submitAgentApplication } from '../api/agent_applications';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  instagram: string;
  state: string;
  licenseStatus: string;
  experience: string;
  callbackSlot: string;
  referralSource: string;
}

interface FormErrors {
  [key: string]: string;
}

// Applicants no longer pick a slot; an owner calls them directly. These notes
// keep the (NOT NULL) interview_date/interview_time columns and the admin
// notification email populated without changing any backend behavior.
const OWNER_CALL_DATE = 'Owner will call applicant';
const OWNER_CALL_TIME = 'their earliest availability';

const Apply: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    instagram: '',
    state: '',
    licenseStatus: '',
    experience: '',
    callbackSlot: `${OWNER_CALL_DATE} at ${OWNER_CALL_TIME}`,
    referralSource: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Required';
    if (!formData.email.trim()) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    if (!formData.state) newErrors.state = 'Required';
    if (!formData.licenseStatus) newErrors.licenseStatus = 'Required';
    if (!formData.experience) newErrors.experience = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const result = await submitAgentApplication({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        instagram: formData.instagram,
        state: formData.state,
        licenseStatus: formData.licenseStatus,
        experience: formData.experience,
        interviewDate: OWNER_CALL_DATE,
        interviewTime: OWNER_CALL_TIME,
        source: 'agent_application_page',
        referralSource: formData.referralSource
      });

      if (result.success) {
        await fetch('https://hook.us2.make.com/u8nt8qper9mun4xfi8k72ja1itl03adx', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, source: 'agent_application_page', timestamp: new Date().toISOString() })
        }).catch(() => {});
        setIsSubmitted(true);
      } else {
        throw new Error(result.error || 'Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    if (field === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
      if (match) {
        const [, area, exchange, number] = match;
        if (number) value = `(${area}) ${exchange}-${number}`;
        else if (exchange) value = `(${area}) ${exchange}`;
        else if (area) value = `(${area}`;
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white pt-28 pb-16 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Application submitted.</h2>
          <p className="text-slate-600 mb-6">
            We'll review your info and reach out soon. Check your email for next steps.
          </p>
          <button
            onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="px-6 py-3 bg-navy-600 text-white rounded-lg hover:bg-navy-700 transition-colors font-medium"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-10">
            <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-2 sm:mb-3">
              Apply to join.
            </h1>
            <p className="text-base sm:text-lg text-slate-600">
              Fill this out and we'll get you on a call. Licensed or not, we'll help you get started.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {/* Contact Info */}
            <div className="space-y-3 sm:space-y-4">
              <h2 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider">Your Info</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange('fullName', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all ${errors.fullName ? 'border-red-400' : 'border-slate-200'}`}
                      placeholder="John Smith"
                    />
                  </div>
                  {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all ${errors.email ? 'border-red-400' : 'border-slate-200'}`}
                      placeholder="john@email.com"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Phone *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      maxLength={14}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all ${errors.phone ? 'border-red-400' : 'border-slate-200'}`}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Instagram</label>
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <input
                      type="text"
                      value={formData.instagram}
                      onChange={(e) => handleInputChange('instagram', e.target.value)}
                      className="w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all"
                      placeholder="@yourhandle"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Background */}
            <div className="space-y-3 sm:space-y-4">
              <h2 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider">Background</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">State *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <select
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all appearance-none text-slate-900 ${errors.state ? 'border-red-400' : 'border-slate-200'}`}
                    >
                      <option value="" className="text-slate-500">Select</option>
                      {siteConfig.states.map(state => <option key={state} value={state} className="text-slate-900">{state}</option>)}
                    </select>
                  </div>
                  {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">License *</label>
                  <div className="relative">
                    <Award className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <select
                      value={formData.licenseStatus}
                      onChange={(e) => handleInputChange('licenseStatus', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all appearance-none text-slate-900 ${errors.licenseStatus ? 'border-red-400' : 'border-slate-200'}`}
                    >
                      <option value="" className="text-slate-500">Select</option>
                      {siteConfig.licenseStatuses.map(status => <option key={status} value={status} className="text-slate-900">{status}</option>)}
                    </select>
                  </div>
                  {errors.licenseStatus && <p className="text-red-500 text-xs mt-1">{errors.licenseStatus}</p>}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Experience *</label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-slate-400" />
                    <select
                      value={formData.experience}
                      onChange={(e) => handleInputChange('experience', e.target.value)}
                      className={`w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base border rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all appearance-none text-slate-900 ${errors.experience ? 'border-red-400' : 'border-slate-200'}`}
                    >
                      <option value="" className="text-slate-500">Select</option>
                      {siteConfig.experienceLevels.map(level => <option key={level} value={level} className="text-slate-900">{level}</option>)}
                    </select>
                  </div>
                  {errors.experience && <p className="text-red-500 text-xs mt-1">{errors.experience}</p>}
                </div>
              </div>
            </div>

            {/* Referral */}
            <div className="space-y-3 sm:space-y-4">
              <h2 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wider">One More Thing</h2>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">How did you hear about us?</label>
                <textarea
                  value={formData.referralSource}
                  onChange={(e) => handleInputChange('referralSource', e.target.value)}
                  rows={2}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all resize-none"
                  placeholder="Social media, a friend, Google, etc."
                />
              </div>
            </div>

            {/* What happens next */}
            <div className="flex items-start gap-3 rounded-lg border border-navy-100 bg-navy-50 p-4 sm:p-5">
              <PhoneCall className="w-5 h-5 text-navy-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-sm sm:text-base font-semibold text-slate-900">What happens next</h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Once you submit, one of our owners will personally give you a call. No scheduling needed, just keep an eye on your phone.
                </p>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 sm:py-4 bg-navy-600 text-white font-semibold text-sm sm:text-base rounded-lg hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Apply;
